import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn } from '@angular/router';
import { map, of, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectCoordinateSystemsLoaded } from '@app/core/coordinate-systems/store/coordinate-systems/coordinate-systems.selectors';
import {
  coordinateSystemsFailure,
  loadCoordinateSystems,
  loadCoordinateSystemsSuccess,
} from '@app/core/coordinate-systems/store/coordinate-systems/coordinate-systems.actions';
import { isEnvFeatureEnabled } from '@app/core/env-feature/env-feature';
import { EnvType } from 'src/environments/common/environment-type.enum';

/**
 * Loads the coordinate system lookup once, alongside the user, so anything rendering
 * `customCoordinates` already has its labels and units.
 *
 * Always resolves to `true`: the lookup only affects how a coordinate pair is rendered (WGS84
 * coordinates stay available), so a failing request must not keep the user out of the app.
 */
export const coordinateSystemsGuard: CanActivateFn | CanMatchFn = () => {
  const store = inject(Store);
  const actions = inject(Actions);

  if (!isEnvFeatureEnabled([EnvType.Development, EnvType.Test])) return of(true);

  return store.select(selectCoordinateSystemsLoaded).pipe(
    take(1),
    switchMap(loaded => {
      if (loaded) {
        return of(true);
      }

      store.dispatch(loadCoordinateSystems());

      return actions.pipe(
        ofType(loadCoordinateSystemsSuccess, coordinateSystemsFailure),
        take(1),
        map(() => true)
      );
    })
  );
};
